<template>
  <div>
    <div :id="id" ref="dropzoneElement" :class="{ dropzone: includeStyling }">
      <div class="dz-message">
        <slot>Drop files here to upload</slot>
      </div>
    </div>
  </div>
</template>

<script>
import Dropzone from "dropzone"; //eslint-disable-line
import { BASE_URL } from "@/store/api";

Dropzone.autoDiscover = false;

export default {
  props: {
    id: {
      type: String,
      default: "dropzone"
    },
    options: {
      type: Object,
      required: true
    },
    includeStyling: {
      type: Boolean,
      default: true
    },
    destroyDropzone: {
      type: Boolean,
      default: true
    },
    duplicateCheck: {
      type: Boolean,
      default: false
    },
    maxFiles: {
      type: Number,
      default: null
    },
    ready: {
      type: Boolean,
      default: true
    },
    url: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      wasQueueAutoProcess: true,
      dropzone: null,
      loaded: false
    };
  },
  computed: {
    dropzoneSettings() {
      let defaultValues = {
        thumbnailMethod: "contain",
        thumbnailWidth: 200,
        thumbnailHeight: 200,
        acceptedFiles: "image/*,.jpg,.jpeg,.png",
        maxFilesize: 1,
        headers: { authorization: this.authHeaders },
        url: this.postURL,
        method: "post"
      };
      Object.keys(this.options).forEach(function(key) {
        defaultValues[key] = this.options[key];
      }, this);
      return defaultValues;
    },
    authHeaders() {
      return `Bearer ${this.$store.state.auth.access}`;
    },
    postURL() {
      return this.url !== null ? this.url : `${BASE_URL}/media/collection/${this.$store.state.shop.settings.media_collection}/upload/`;
    }
  },
  watch: {
    ready(newValue) {
      if (this.ready && !this.loaded) {
        this.construct();
      }
    }
  },
  mounted() {
    if (this.ready) {
      this.construct();
    }
  },
  beforeDestroy() {
    if (this.destroyDropzone && this.loaded) {
      this.dropzone.destroy();
    }
  },
  methods: {
    construct() {
      this.loaded = true;

      this.dropzone = new Dropzone(
        this.$refs.dropzoneElement,
        this.dropzoneSettings
      );
      let vm = this;

      this.dropzone.on("thumbnail", function(file, dataUrl) {
        vm.$emit("thumbnail", file, dataUrl);
      });

      this.dropzone.on("addedfile", function(file) {
        var isDuplicate = false;
        if (vm.duplicateCheck) {
          if (this.files.length) {
            var _i, _len;
            for (
              _i = 0, _len = this.files.length;
              _i < _len - 1;
              _i++ // -1 to exclude current file
            ) {
              if (
                this.files[_i].name === file.name &&
                this.files[_i].size === file.size &&
                this.files[_i].lastModifiedDate.toString() ===
                  file.lastModifiedDate.toString()
              ) {
                this.removeFile(file);
                isDuplicate = true;
                vm.$emit("duplicate-file", file);
              }
            }
          }
        }
        if (vm.maxFiles !== null) {
          while (this.files.length > vm.maxFiles) {
            this.removeFile(this.files[0]);
          }
        }

        vm.$emit("file-added", file);
      });

      this.dropzone.on("addedfiles", function(files) {
        vm.$emit("files-added", files);
      });

      this.dropzone.on("removedfile", function(file) {
        vm.$emit("removed-file", file);
        if (file.manuallyAdded && vm.dropzone.options.maxFiles !== null)
          vm.dropzone.options.maxFiles++;
      });

      this.dropzone.on("success", function(file, response) {
        vm.$emit("success", file, response);
      });

      this.dropzone.on("successmultiple", function(file, response) {
        vm.$emit("success-multiple", file, response);
      });

      this.dropzone.on("error", function(file, message, xhr) {
        vm.$emit("error", file, message, xhr);
      });

      this.dropzone.on("errormultiple", function(files, message, xhr) {
        vm.$emit("error-multiple", files, message, xhr);
      });

      this.dropzone.on("sending", function(file, xhr, formData) {
        vm.$emit("sending", file, xhr, formData);
      });

      this.dropzone.on("sendingmultiple", function(file, xhr, formData) {
        vm.$emit("sending-multiple", file, xhr, formData);
      });

      this.dropzone.on("complete", function(file) {
        vm.$emit("complete", file);
      });

      this.dropzone.on("completemultiple", function(files) {
        vm.$emit("complete-multiple", files);
      });

      this.dropzone.on("canceled", function(file) {
        vm.$emit("canceled", file);
      });

      this.dropzone.on("canceledmultiple", function(files) {
        vm.$emit("canceled-multiple", files);
      });

      this.dropzone.on("maxfilesreached", function(files) {
        vm.$emit("max-files-reached", files);
      });

      this.dropzone.on("maxfilesexceeded", function(file) {
        vm.$emit("max-files-exceeded", file);
      });

      this.dropzone.on("processing", function(file) {
        vm.$emit("processing", file);
      });

      this.dropzone.on("processingmultiple", function(files) {
        vm.$emit("processing-multiple", files);
      });

      this.dropzone.on("uploadprogress", function(file, progress, bytesSent) {
        vm.$emit("upload-progress", file, progress, bytesSent);
      });

      this.dropzone.on("totaluploadprogress", function(
        totaluploadprogress,
        totalBytes,
        totalBytesSent
      ) {
        vm.$emit(
          "total-upload-progress",
          totaluploadprogress,
          totalBytes,
          totalBytesSent
        );
      });

      this.dropzone.on("reset", function() {
        vm.$emit("vdropzone-reset");
      });

      this.dropzone.on("queuecomplete", function() {
        vm.$emit("queue-complete");
      });

      this.dropzone.on("drop", function(event) {
        vm.$emit("drop", event);
      });

      this.dropzone.on("dragstart", function(event) {
        vm.$emit("drag-start", event);
      });

      this.dropzone.on("dragend", function(event) {
        vm.$emit("drag-end", event);
      });

      this.dropzone.on("dragenter", function(event) {
        vm.$emit("drag-enter", event);
      });

      this.dropzone.on("dragover", function(event) {
        vm.$emit("drag-over", event);
      });

      this.dropzone.on("dragleave", function(event) {
        vm.$emit("drag-leave", event);
      });
    },
    manuallyAddFile: function(file, fileUrl) {
      file.manuallyAdded = true;
      this.dropzone.emit("addedfile", file);
      let containsImageFileType = false;
      if (
        fileUrl.indexOf(".svg") > -1 ||
        fileUrl.indexOf(".png") > -1 ||
        fileUrl.indexOf(".jpg") > -1 ||
        fileUrl.indexOf(".jpeg") > -1 ||
        fileUrl.indexOf(".gif") > -1 ||
        fileUrl.indexOf(".webp") > -1
      )
        containsImageFileType = true;
      if (
        this.dropzone.options.createImageThumbnails &&
        containsImageFileType &&
        file.size <= this.dropzone.options.maxThumbnailFilesize * 1024 * 1024
      ) {
        fileUrl && this.dropzone.emit("thumbnail", file, fileUrl);

        var thumbnails = file.previewElement.querySelectorAll(
          "[data-dz-thumbnail]"
        );
        for (var i = 0; i < thumbnails.length; i++) {
          thumbnails[i].style.width =
            this.dropzoneSettings.thumbnailWidth + "px";
          thumbnails[i].style.height =
            this.dropzoneSettings.thumbnailHeight + "px";
          thumbnails[i].style["object-fit"] = "contain";
        }
      }
      this.dropzone.emit("complete", file);
      if (this.dropzone.options.maxFiles) this.dropzone.options.maxFiles--;
      this.dropzone.files.push(file);
      this.$emit("file-added-manually", file);
    },
    setOption: function(option, value) {
      this.dropzone.options[option] = value;
    },
    removeAllFiles: function(bool) {
      this.dropzone.removeAllFiles(bool);
    },
    processQueue: function() {
      let dropzoneEle = this.dropzone;

      this.dropzone.processQueue();

      this.dropzone.on("success", function() {
        dropzoneEle.options.autoProcessQueue = true;
      });
      this.dropzone.on("queuecomplete", function() {
        dropzoneEle.options.autoProcessQueue = false;
      });
    },
    init: function() {
      return this.dropzone.init();
    },
    destroy: function() {
      return this.dropzone.destroy();
    },
    updateTotalUploadProgress: function() {
      return this.dropzone.updateTotalUploadProgress();
    },
    getFallbackForm: function() {
      return this.dropzone.getFallbackForm();
    },
    getExistingFallback: function() {
      return this.dropzone.getExistingFallback();
    },
    setupEventListeners: function() {
      return this.dropzone.setupEventListeners();
    },
    removeEventListeners: function() {
      return this.dropzone.removeEventListeners();
    },
    disable: function() {
      return this.dropzone.disable();
    },
    enable: function() {
      return this.dropzone.enable();
    },
    filesize: function(size) {
      return this.dropzone.filesize(size);
    },
    accept: function(file, done) {
      return this.dropzone.accept(file, done);
    },
    addFile: function(file) {
      return this.dropzone.addFile(file);
    },
    removeFile: function(file) {
      this.dropzone.removeFile(file);
    },
    getAcceptedFiles: function() {
      return this.dropzone.getAcceptedFiles();
    },
    getRejectedFiles: function() {
      return this.dropzone.getRejectedFiles();
    },
    getFilesWithStatus: function() {
      return this.dropzone.getFilesWithStatus();
    },
    getQueuedFiles: function() {
      return this.dropzone.getQueuedFiles();
    },
    getUploadingFiles: function() {
      return this.dropzone.getUploadingFiles();
    },
    getAddedFiles: function() {
      return this.dropzone.getAddedFiles();
    },
    getActiveFiles: function() {
      return this.dropzone.getActiveFiles();
    }
  }
};
</script>

<style>
.dropzone {
  border: 1px solid #e5e5e5 !important;
  border-radius: 0.15rem;
  color: #333;
  transition: 0.2s linear;
}
</style>
